import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import {
  AddressFields,
  FIELD_CLASS,
  type AddressValue,
} from '@/components/properties/AddressFields';
import { useAddProperty } from '@/routes/customers/useCustomerActions';

/**
 * "Add property" — one of the customer page's quick actions (M1-04).
 *
 * The fields are exactly M1-02's `CreatePropertyRequest` and no more. Access
 * notes and the gate code are here rather than left to an edit afterwards
 * because they are what a technician standing at the door needs (M1-05), and
 * the moment someone knows a new address is the moment they know its gate
 * code.
 *
 * The address block is `AddressFields` (M1-23): autocomplete on the street
 * line that fills city, state and ZIP in one pick, with plain typing still
 * working when the suggestions are wrong or unavailable. The API geocodes the
 * saved address; nothing here holds a coordinate or a Maps credential.
 *
 * Required-ness is stated in HTML (`required`) as well as enforced by the API.
 * The browser's own validation is the fast path; the API's 400 is the real
 * one, and its message is what the error line shows.
 */
export function AddPropertyDialog({
  customerId,
  onClose,
  onAdded,
}: {
  customerId: string;
  onClose: () => void;
  /** Announced by the page, which owns the live region. */
  onAdded: (line1: string) => void;
}) {
  const { t } = useTranslation();
  const addProperty = useAddProperty(customerId);

  const [address, setAddress] = useState<AddressValue>({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
  });
  const [unit, setUnit] = useState('');
  const [accessNotes, setAccessNotes] = useState('');
  const [gateCode, setGateCode] = useState('');

  const canSubmit =
    address.line1.trim() !== '' &&
    address.city.trim() !== '' &&
    address.state.trim() !== '' &&
    address.postalCode.trim() !== '';

  function submit() {
    addProperty.mutate(
      {
        address: {
          line1: address.line1.trim(),
          line2: address.line2.trim() || null,
          city: address.city.trim(),
          state: address.state.trim(),
          postalCode: address.postalCode.trim(),
        },
        unit: unit.trim() || null,
        accessNotes: accessNotes.trim() || null,
        gateCode: gateCode.trim() || null,
        // General property notes have no field here — M1-05 added them for
        // the edit form on the property's own detail page, once it exists to
        // edit from. Unlike access notes/gate code, there's no "the moment
        // you know the address" urgency for them.
        notes: null,
      },
      {
        onSuccess: () => {
          onAdded(address.line1.trim());
          onClose();
        },
      },
    );
  }

  return (
    <Dialog
      titleId="add-property-title"
      title={t('customerDetail.addProperty.title')}
      description={t('customerDetail.addProperty.description')}
      onClose={onClose}
      testId="add-property-dialog"
      dismissLabel={t('customerDetail.addProperty.dismiss')}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canSubmit || addProperty.isPending}
            onClick={submit}
          >
            {addProperty.isPending
              ? t('customerDetail.addProperty.saving')
              : t('customerDetail.addProperty.confirm')}
          </Button>
        </>
      }
    >
      <AddressFields
        value={address}
        onChange={setAddress}
        labels={{
          line1: t('customerDetail.addProperty.line1'),
          line2: t('customerDetail.addProperty.line2'),
          city: t('customerDetail.addProperty.city'),
          state: t('customerDetail.addProperty.state'),
          postalCode: t('customerDetail.addProperty.postalCode'),
        }}
      >
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('customerDetail.addProperty.unit')}</span>
          <input
            className={FIELD_CLASS}
            value={unit}
            onChange={(event) => setUnit(event.target.value)}
          />
        </label>
      </AddressFields>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('customerDetail.addProperty.gateCode')}</span>
        <input
          className={FIELD_CLASS}
          value={gateCode}
          onChange={(event) => setGateCode(event.target.value)}
        />
      </label>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">
          {t('customerDetail.addProperty.accessNotes')}
        </span>
        <textarea
          rows={2}
          className="w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 py-1.5 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
          value={accessNotes}
          onChange={(event) => setAccessNotes(event.target.value)}
        />
      </label>

      {addProperty.isError ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('customerDetail.addProperty.failed')}
        </p>
      ) : null}
    </Dialog>
  );
}
